/**
 * Numbers and percentages (TMMPDM-0065, TMM-B1 / TMM-L4). Every number a person reads goes through
 * Intl so the separators follow the locale; "—" is a value the API didn't send, a sent 0 shows 0.
 */
import { NOT_SENT, formatDecimal, type Maybe } from './money';

/** A count or plain number with the locale's grouping. */
export function formatNumber(n: Maybe<number>, locale: string): string {
  if (n === null || n === undefined) return NOT_SENT;
  return new Intl.NumberFormat(locale).format(n);
}

/**
 * Basis points as a percent. With no `fractionDigits` it is Intl's percent style, up to two
 * decimals ("12.5%"); with `fractionDigits` it is a fixed-decimal number and a spaced "%"
 * ("12.50 %"), the form the rate-card and statement tables use.
 */
export function formatPercentBp(
  bp: Maybe<number>,
  locale: string,
  fractionDigits?: number,
): string {
  if (bp === null || bp === undefined) return NOT_SENT;
  if (fractionDigits !== undefined) {
    return `${formatDecimal(bp / 100, locale, fractionDigits, fractionDigits)} %`;
  }
  try {
    return new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 2 }).format(
      bp / 10000,
    );
  } catch {
    return `${bp / 100}%`;
  }
}

const FILE_SIZE_UNITS = ['byte', 'kilobyte', 'megabyte', 'gigabyte'] as const;

/**
 * A file's size (TMMPDM-0110, ticket attachments): bytes in the largest unit that keeps the value
 * at 1 or more (steps of 1,024), Intl's short unit style ("512 byte", "1.5 kB", "10 MB"), at most
 * one decimal.
 */
export function formatFileSize(bytes: Maybe<number>, locale: string): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes) || bytes < 0) {
    return NOT_SENT;
  }
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < FILE_SIZE_UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }
  try {
    return new Intl.NumberFormat(locale, {
      style: 'unit',
      unit: FILE_SIZE_UNITS[unit],
      unitDisplay: 'short',
      maximumFractionDigits: unit === 0 ? 0 : 1,
    }).format(value);
  } catch {
    return new Intl.NumberFormat(locale).format(bytes);
  }
}

/**
 * A rate the server sent as a fraction of 1 (0.85) as Intl's percent, up to one decimal ("85%",
 * "12.5%") — TMMPDM-0112, the naming-quality report. Formatting only: the rate is the server's.
 */
export function formatRatioPercent(ratio: Maybe<number>, locale: string): string {
  if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) return NOT_SENT;
  try {
    return new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 1 }).format(
      ratio,
    );
  } catch {
    return `${Math.round(ratio * 1000) / 10}%`;
  }
}
